import React, { useRef, useState } from "react";
import { X, Upload, Trash2, Image as ImageIcon } from "lucide-react";
import { Arma } from "@/lib/armaApi";
import { useInstance } from "@/context/InstanceContext";
import TacticalBadge from "./TacticalBadge";

export default function BrandingModal({ open, onClose }) {
  const { branding, refreshBranding } = useInstance();
  const [serverName, setServerName] = useState(branding.serverName);
  const [tagline, setTagline] = useState(branding.tagline);
  const [logoPreview, setLogoPreview] = useState(null);
  const [bgPreview, setBgPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const logoRef = useRef(null);
  const bgRef = useRef(null);

  React.useEffect(() => {
    if (open) {
      setServerName(branding.serverName);
      setTagline(branding.tagline);
      setLogoPreview(null);
      setBgPreview(null);
      setErr(null);
    }
  }, [open, branding]);

  if (!open) return null;

  const onLogoFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 4 * 1024 * 1024) return setErr("Logo max. 4 MB");
    setErr(null);
    setLogoPreview(URL.createObjectURL(f));
  };

  const onBgFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 12 * 1024 * 1024) return setErr("Wallpaper max. 12 MB");
    setErr(null);
    setBgPreview(URL.createObjectURL(f));
  };

  const onSave = async () => {
    setBusy(true);
    setErr(null);
    try {
      const fd = new FormData();
      fd.append("serverName", serverName);
      fd.append("tagline", tagline);
      const logoF = logoRef.current?.files?.[0];
      if (logoF) fd.append("logo", logoF);
      const bgF = bgRef.current?.files?.[0];
      if (bgF) fd.append("background", bgF);
      await Arma.updateBranding(fd);
      refreshBranding();
      onClose();
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Fehler");
    } finally {
      setBusy(false);
    }
  };

  const onRemoveLogo = async () => {
    setBusy(true);
    try { await Arma.deleteLogo(); refreshBranding(); setLogoPreview(null); if (logoRef.current) logoRef.current.value = ""; }
    catch (e) { setErr(e?.message || "Fehler"); }
    finally { setBusy(false); }
  };

  const onRemoveBg = async () => {
    setBusy(true);
    try { await Arma.deleteBackground(); refreshBranding(); setBgPreview(null); if (bgRef.current) bgRef.current.value = ""; }
    catch (e) { setErr(e?.message || "Fehler"); }
    finally { setBusy(false); }
  };

  const currentLogo = logoPreview || branding.logoUrl;
  const currentBg = bgPreview || branding.backgroundUrl;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 p-4 overflow-y-auto">
      <div className="tac-panel corners w-full max-w-lg">
        <span className="corner-tr" />
        <span className="corner-bl" />
        <div className="tac-panel-header">
          <div className="label-tech" style={{ color: "var(--tan-bright)" }}>UNIT BRANDING / EDIT</div>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-100">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5 space-y-5">
          {/* Logo */}
          <div className="flex items-center gap-4">
            <div className="shrink-0 w-20 h-20 flex items-center justify-center bg-[var(--bg)] border border-[var(--border)]">
              {currentLogo ? (
                <img src={currentLogo} alt="logo" className="max-w-full max-h-full object-contain" />
              ) : (
                <TacticalBadge
                  size={72}
                  label={(serverName || "BOYS").split(" ")[0]}
                  sub={(serverName || "BOYS OPS").split(" ")[1] || "OPS"}
                />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="label-tech mb-1">EINHEITS-PATCH / LOGO</div>
              <div className="text-[11px] text-[var(--text-muted)] mb-2">PNG/SVG/WEBP transparent. Max. 4 MB.</div>
              <div className="flex gap-2">
                <button type="button" onClick={() => logoRef.current?.click()} className="btn-mil btn-mil-outline">
                  <Upload className="w-3.5 h-3.5" />Datei wählen
                </button>
                {(branding.logoUrl || logoPreview) && (
                  <button type="button" onClick={onRemoveLogo} disabled={busy} className="btn-mil btn-mil-outline"
                    style={{ color: "var(--red-bright)", borderColor: "var(--red)" }}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              <input ref={logoRef} type="file" accept=".png,.jpg,.jpeg,.svg,.webp,.gif" onChange={onLogoFile} className="hidden" />
            </div>
          </div>

          {/* Background / wallpaper */}
          <div className="flex items-center gap-4">
            <div className="shrink-0 w-20 h-20 flex items-center justify-center bg-[var(--bg)] border border-[var(--border)] overflow-hidden">
              {currentBg ? (
                <img src={currentBg} alt="bg" className="w-full h-full object-cover" />
              ) : (
                <ImageIcon className="w-7 h-7" style={{ color: "var(--text-faint)" }} />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="label-tech mb-1">HERO WALLPAPER</div>
              <div className="text-[11px] text-[var(--text-muted)] mb-2">JPG/PNG, mind. 1920×1080. Max. 12 MB.</div>
              <div className="flex gap-2">
                <button type="button" onClick={() => bgRef.current?.click()} className="btn-mil btn-mil-outline">
                  <Upload className="w-3.5 h-3.5" />Wallpaper wählen
                </button>
                {(branding.backgroundUrl || bgPreview) && (
                  <button type="button" onClick={onRemoveBg} disabled={busy} className="btn-mil btn-mil-outline"
                    style={{ color: "var(--red-bright)", borderColor: "var(--red)" }}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              <input ref={bgRef} type="file" accept=".png,.jpg,.jpeg,.webp" onChange={onBgFile} className="hidden" />
            </div>
          </div>

          <div>
            <label className="label-tech block mb-1">EINHEITS-NAME</label>
            <input type="text" value={serverName} onChange={(e) => setServerName(e.target.value)} maxLength={64}
              className="w-full bg-[var(--bg)] border border-[var(--border)] px-3 py-2 text-[var(--text)] font-stencil tracking-wide focus:outline-none focus:border-[var(--tan-bright)]" />
          </div>
          <div>
            <label className="label-tech block mb-1">TAGLINE</label>
            <input type="text" value={tagline} onChange={(e) => setTagline(e.target.value)} maxLength={96}
              className="w-full bg-[var(--bg)] border border-[var(--border)] px-3 py-2 text-[var(--text)] font-mono-tech text-xs focus:outline-none focus:border-[var(--tan-bright)]" />
          </div>

          {err && (
            <div className="text-xs font-mono-tech px-3 py-2 border" style={{ borderColor: "var(--red)", color: "var(--red-bright)", background: "rgba(178, 31, 31, 0.1)" }}>
              {err}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2 border-t" style={{ borderColor: "var(--border-faint)" }}>
            <button onClick={onClose} className="btn-mil btn-mil-outline" disabled={busy}>Abbrechen</button>
            <button onClick={onSave} className="btn-mil" disabled={busy}>
              {busy ? "Speichert …" : "Übernehmen"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
